/* eslint-disable no-unused-vars */
import { cn } from "@aichat/ui/lib/utils";
import { ActionRating } from "../rating";
import { Typography } from "../typography";
import { ActionVoting } from "../voting";
import { ActionAdditionalFeedback } from "../additional-feedback";
import { Button } from "../button";
import { ReactNode, useEffect, useState } from "react";
import { useWebWidgetConfig } from "./use-web-widget-config";
import { getHSLAString } from "@aichat/aichat-lib";
import SubmittedSurvey from "../submitted-survey";
import { SurveyComponent } from "@aichat/types/socket/widget.types";
import { CustomerSurveyPhaseEnum, VoteTypeEnum } from "@aichat/types/index";
import { SubmitSurveyParams } from "@aichat/types/customer-survey.types";

type FeedbackSurveyProps = {
  customerSurvey: SurveyComponent | null;
  onFinish: (payload: SubmitSurveyParams) => Promise<void>;
};

const FeedbackSurvey = ({ customerSurvey, onFinish }: FeedbackSurveyProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phase, setPhase] = useState<CustomerSurveyPhaseEnum>(
    CustomerSurveyPhaseEnum.default,
  );
  const [localCustomerSurvey, setLocalCustomerSurvey] =
    useState<SurveyComponent | null>(null);
  const [csatRating, setCsatRating] = useState(0);
  const [npsVoting, setNpsVoting] = useState<boolean | null>(null);
  const [additionalFeedback, setAdditionalFeedback] = useState("");

  useEffect(() => {
    if (!customerSurvey) return;
    setLocalCustomerSurvey(customerSurvey);
    setPhase(
      customerSurvey.component.csat.enabled
        ? CustomerSurveyPhaseEnum.csat
        : CustomerSurveyPhaseEnum.nps,
    );
  }, [customerSurvey]);

  const { brandColor, textColor } = useWebWidgetConfig();
  const brandColorHSLA = getHSLAString(brandColor);

  const handleNext = async ({
    skip,
    value,
  }: {
    skip?: boolean;
    value?: string | number | null | boolean;
  }) => {
    if (phase === CustomerSurveyPhaseEnum.csat) {
      if (!skip) setCsatRating(value as number);
      setPhase(
        localCustomerSurvey?.component.nps.enabled
          ? CustomerSurveyPhaseEnum.nps
          : CustomerSurveyPhaseEnum.additional,
      );
      return;
    }

    if (phase === CustomerSurveyPhaseEnum.nps) {
      if (!skip) setNpsVoting(value === VoteTypeEnum.likely);
      setPhase(CustomerSurveyPhaseEnum.additional);
      return;
    }

    if (phase === CustomerSurveyPhaseEnum.additional) {
      setIsSubmitting(true);
      await onFinish({
        csatRating: csatRating || null,
        npsVoting,
        additionalFeedback: skip ? "" : additionalFeedback,
        usedFor: customerSurvey?.usedFor ?? "BOT",
        liveAgentId: customerSurvey?.previousAgentInfo?.agentId,
        liveAgentName: customerSurvey?.previousAgentInfo?.agentName,
      });
      setIsSubmitting(false);
      setPhase(CustomerSurveyPhaseEnum.finish);
      setTimeout(() => {
        onFinish({
          close: true,
          csatRating,
          npsVoting,
          additionalFeedback,
          liveAgentId: customerSurvey?.previousAgentInfo?.agentId,
          liveAgentName: customerSurvey?.previousAgentInfo?.agentName,
          usedFor: customerSurvey?.usedFor ?? "BOT",
        });
      }, 5000);
    }
  };

  const handleReset = () => {
    setPhase(CustomerSurveyPhaseEnum.finish);
    setLocalCustomerSurvey(null);
    setCsatRating(0);
    setNpsVoting(null);
    setAdditionalFeedback("");
  };

  const handleClose = async () => {
    const canClose = phase === CustomerSurveyPhaseEnum.finish && !isSubmitting;
    if (!canClose) return;
    await onFinish({
      close: true,
      csatRating,
      npsVoting,
      additionalFeedback,
      liveAgentId: customerSurvey?.previousAgentInfo?.agentId,
      liveAgentName: customerSurvey?.previousAgentInfo?.agentName,
      usedFor: customerSurvey?.usedFor ?? "BOT",
    });
    handleReset();
  };

  const ActionMapping: Record<CustomerSurveyPhaseEnum, ReactNode> = {
    default: <></>,
    csat: (
      <ActionRating
        wrapperClassName="p-0"
        rating={csatRating}
        text={customerSurvey?.component.csat.question_title}
        className="no-underline"
        variant="yellow"
        onRatingChange={(rating) => handleNext({ value: rating })}
      />
    ),
    nps: (
      <ActionVoting
        wrapperClassName="p-0"
        className="no-underline"
        text={customerSurvey?.component.nps.question_title}
        onChange={(vote) =>
          handleNext({
            value: vote,
          })
        }
      />
    ),
    additional: (
      <ActionAdditionalFeedback
        wrapperClassName="p-0"
        text={customerSurvey?.component.additional_feedback_title}
        className="no-underline"
        onChange={(e) => setAdditionalFeedback(e.target.value)}
      />
    ),
    finish: <SubmittedSurvey className="p-0" />,
  };

  return (
    <div
      className={cn(
        "absolute z-[99999] inset-0 bg-black bg-opacity-35 p-2 flex items-end animate-scale-up-right scale-0 fill-mode-forwards",
      )}
      onClick={() => handleClose()}
    >
      <div
        className={cn(
          "bg-white text-center flex flex-col  rounded-xl w-full p-8",
          phase === "additional" ? "gap-8" : "gap-2",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {ActionMapping[phase]}
        <div className="flex flex-col gap-2">
          {phase === "additional" && (
            <Button
              loading={isSubmitting}
              style={{
                background: brandColorHSLA,
                color: textColor === "light" ? "white" : "black",
              }}
              onClick={() =>
                handleNext({
                  value: additionalFeedback,
                })
              }
            >
              Submit Feedback
            </Button>
          )}
          {phase && phase !== "finish" && (
            <Button
              variant="ghost"
              onClick={() =>
                handleNext({
                  skip: true,
                })
              }
              loading={isSubmitting}
            >
              <Typography variant="subtleMedium">Skip for now</Typography>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackSurvey;
